/**
 * pdfWorker.js — Fix Round v5 (2026-07-11).
 *
 * Single source of truth for pdf.js worker configuration for the
 * drawing-thumbnail pipeline. The old code pointed `workerSrc` at a
 * CDN which failed
 * on production for weeks — the CDN blocked, the version drifted,
 * and the failure was masked as a generic "render failed" string.
 *
 * The fix, per user request:
 *   • Worker is copied from `node_modules/pdfjs-dist/build/pdf.worker.min.mjs`
 *     into `public/pdf.worker.min.mjs` at build time by
 *     `scripts/sync-pdf-worker.js`. It is served same-origin, no CDN.
 *   • The build script also emits `src/generated/pdfWorkerBuildInfo.js`
 *     with the exact `pdfjs-dist` version. At runtime we assert the
 *     API version (`pdfjs.version`) equals the pinned worker version,
 *     so a future `yarn upgrade pdfjs-dist` without re-running the
 *     sync fails loudly instead of silently.
 *   • A one-shot startup self-test renders a tiny inline base-64 PDF.
 *     If it fails, the real `err.name` + `err.message` are exposed to
 *     the debug overlay of every thumbnail card.
 */
import { pdfjs } from 'react-pdf';
import {
  PDF_WORKER_BUILD_VERSION,
  PDF_WORKER_BUILD_SIZE,
  PDF_WORKER_BUILD_SHA256,
} from '../generated/pdfWorkerBuildInfo';

// A minimal syntactically-valid single-page PDF, ~317 bytes. Generated
// once offline and hard-coded here so the self-test is self-contained —
// no network, no static asset, no bundler quirks.
const TEST_PDF_B64 =
  'JVBERi0xLjEKMSAwIG9iajw8L1R5cGUvQ2F0YWxvZy9QYWdlcyAyIDAgUj4+ZW5kb2Jq' +
  'CjIgMCBvYmo8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PmVuZG9iago' +
  'zIDAgb2JqPDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgMTAwI' +
  'DEwMF0vUmVzb3VyY2VzPDw+Pj4+ZW5kb2JqCnhyZWYKMCA0CjAwMDAwMDAwMDAgNjU' +
  '1MzUgZiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMDUyIDAwMDAwIG4gCjAwM' +
  'DAwMDAxMDEgMDAwMDAgbiAKdHJhaWxlcjw8L1NpemUgNC9Sb290IDEgMCBSPj4Kc3R' +
  'hcnR4cmVmCjE3OAolJUVPRgo=';

function base64ToBytes(b64) {
  const clean = b64.replace(/\s+/g, '');
  const bin = typeof atob === 'function' ? atob(clean) : Buffer.from(clean, 'base64').toString('binary');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

// The public URL is set by CRA at build time. Falls back to '/' for
// dev boxes where PUBLIC_URL isn't set.
function workerUrl() {
  const base = process.env.PUBLIC_URL || '';
  return `${base}/pdf.worker.min.mjs`;
}

let INITIALISED = false;
let VERSION_MISMATCH = null; // { apiVersion, workerVersion } or null

const SELF_TEST = {
  status: 'pending',        // pending | ok | failed | skipped
  errorName: null,
  errorMessage: null,
  ranAt: null,
  workerSrc: null,
  apiVersion: null,
  workerBuildVersion: null,
  workerBuildSize: null,
  workerBuildSha: null,
};

const SUBS = new Set();
function notify() {
  SUBS.forEach((cb) => { try { cb(getSelfTestState()); } catch { /* noop */ } });
}

/**
 * Public: idempotent init. Safe to call from many modules — the
 * heavy work only runs once.
 */
export function initPdfWorker() {
  if (INITIALISED) return;
  INITIALISED = true;

  const src = workerUrl();
  pdfjs.GlobalWorkerOptions.workerSrc = src;
  SELF_TEST.workerSrc = src;
  SELF_TEST.apiVersion = pdfjs.version;
  SELF_TEST.workerBuildVersion = PDF_WORKER_BUILD_VERSION;
  SELF_TEST.workerBuildSize = PDF_WORKER_BUILD_SIZE;
  SELF_TEST.workerBuildSha = PDF_WORKER_BUILD_SHA256;

  if (pdfjs.version && PDF_WORKER_BUILD_VERSION && pdfjs.version !== PDF_WORKER_BUILD_VERSION) {
    VERSION_MISMATCH = {
      apiVersion: pdfjs.version,
      workerVersion: PDF_WORKER_BUILD_VERSION,
    };
    console.error(
      `[pdfWorker] VERSION MISMATCH: pdfjs API v${pdfjs.version} vs bundled ` +
      `worker v${PDF_WORKER_BUILD_VERSION}. Run \`yarn start\` (which runs ` +
      `scripts/sync-pdf-worker.js) or rebuild the frontend.`,
    );
  }

  // DP278-T2a-4 — dev only: read the version string out of the worker we
  // actually serve. A stale public/pdf.worker.min.mjs is the failure that
  // showed up as an endless "Loading…", so say it out loud instead.
  if (process.env.NODE_ENV !== 'production' && typeof fetch === 'function') {
    (async () => {
      try {
        const resp = await fetch(src);
        if (!resp.ok) {
          console.error(`[pdfWorker] worker not served at ${src} — HTTP ${resp.status}. Run \`node scripts/sync-pdf-worker.js\`.`);
          return;
        }
        const text = await resp.text();
        const stamped = Array.from(new Set(text.match(/\d+\.\d+\.\d+/g) || []));
        if (!stamped.includes(pdfjs.version)) {
          console.error(
            `[pdfWorker] VERSION MISMATCH: ${src} carries ${stamped.join(', ') || 'no version string'} ` +
            `but the API is v${pdfjs.version}. Run \`node scripts/sync-pdf-worker.js\`.`,
          );
        }
      } catch (err) {
        console.error('[pdfWorker] could not verify the served worker:', err);
      }
    })();
  }

  (async () => {
    try {
      const data = base64ToBytes(TEST_PDF_B64);
      // Verbosity=0 to silence normal pdf.js banner. Keep the worker
      // authoritative — no `disableWorker: true` fallback (we WANT it
      // to fail here if the worker is broken).
      const task = pdfjs.getDocument({ data, verbosity: 0 });
      const doc = await task.promise;
      const page = await doc.getPage(1);
      // Do a tiny render to a 1×1 canvas to exercise the full pipeline
      // (worker → canvas), not just parsing.
      const vp = page.getViewport({ scale: 0.01 });
      const canvas = (typeof document !== 'undefined')
        ? document.createElement('canvas')
        : null;
      if (canvas) {
        canvas.width = Math.max(1, Math.ceil(vp.width));
        canvas.height = Math.max(1, Math.ceil(vp.height));
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      }
      try { doc.destroy(); } catch { /* noop */ }

      SELF_TEST.status = VERSION_MISMATCH ? 'failed' : 'ok';
      SELF_TEST.errorName = VERSION_MISMATCH ? 'VersionMismatch' : null;
      SELF_TEST.errorMessage = VERSION_MISMATCH
        ? `API v${VERSION_MISMATCH.apiVersion} ≠ worker v${VERSION_MISMATCH.workerVersion}`
        : null;
    } catch (err) {
      SELF_TEST.status = 'failed';
      SELF_TEST.errorName = err && err.name ? String(err.name) : 'Error';
      SELF_TEST.errorMessage = err && err.message ? String(err.message) : String(err);
      console.error('[pdfWorker] self-test failed:', err);
    } finally {
      SELF_TEST.ranAt = new Date().toISOString();
      notify();
    }
  })();
}

export function getSelfTestState() {
  return { ...SELF_TEST };
}

export function subscribeSelfTest(cb) {
  SUBS.add(cb);
  return () => SUBS.delete(cb);
}

/**
 * Public: authed fetch of the thumbnail-health rollup for one project.
 * Wired to a debug-overlay button so super-admins can trigger it (raw
 * URL access 401s because there's no way to attach the bearer).
 */
export async function fetchThumbnailHealth(projectId) {
  if (!projectId) return { ok: false, error: 'no project_id' };
  try {
    const token = (typeof window !== 'undefined' && window.localStorage)
      ? (window.localStorage.getItem('accessToken') || window.localStorage.getItem('token'))
      : null;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const resp = await fetch(
      `${base}/api/projects/${encodeURIComponent(projectId)}/drawings/thumbnail-health`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    const body = await resp.json().catch(() => null);
    if (!resp.ok) return { ok: false, status: resp.status, body };
    return { ok: true, status: resp.status, body };
  } catch (err) {
    return { ok: false, error: err && err.message ? err.message : String(err) };
  }
}

// Auto-init on module load so the worker is ready before any card
// renders. Modules that need the worker should still import this file
// (which triggers this side-effect) rather than rely on ordering.
initPdfWorker();
